<template>
  <transition name="bounce">
    <b-container class="py-5 custom-container container">
      <b-breadcrumb :items="items" class="pl-0 pt-5 mt-5 bg-transparent" />

      <b-card>
        <div
          class="d-flex justify-content-between align-items-start mb-3 message-log-header"
        >
          <h2 class="font-weight-bold mb-0 message-log-title">
            Message Log Insight
          </h2>
          <b-button
            variant="outline-primary"
            class="message-log-back-btn"
            @click="goBackToRequests"
          >
            Back to Requests
          </b-button>
        </div>

        <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

        <div v-else-if="loading" class="text-center py-4">
          <digger-loader />
        </div>

        <div v-else-if="log">
          <b-row>
            <b-col md="8" class="message-log-body-col">
              <h4 class="font-weight-bold message-log-notification-title">
                {{ titleText }}
              </h4>
              <p class="mb-4 white-space-pre-line">
                <span v-html="messageHtml"></span>
              </p>
            </b-col>

            <b-col md="4" class="message-log-meta-col">
              <b-list-group flush>
                <b-list-group-item class="px-0">
                  <strong>Sent:</strong> {{ sentAtText }}
                </b-list-group-item>
                <b-list-group-item class="px-0">
                  <strong>Admin:</strong> {{ senderText }}
                </b-list-group-item>
                <b-list-group-item class="px-0">
                  <strong>Project:</strong> {{ projectText }}
                </b-list-group-item>
                <b-list-group-item class="px-0">
                  <strong>Recipients:</strong> {{ recipientCountText }}
                </b-list-group-item>
                <b-list-group-item class="px-0">
                  <strong>Read:</strong> {{ readCount }}
                </b-list-group-item>
                <b-list-group-item class="px-0">
                  <strong>Not Read:</strong> {{ unreadCount }}
                </b-list-group-item>
              </b-list-group>
            </b-col>
          </b-row>
        </div>

        <b-alert v-else show variant="warning">Message log not found.</b-alert>
      </b-card>

      <div class="mt-4">
        <b-card no-body>
          <b-tabs
            v-model="tabIndex"
            class="request-tab"
            content-class="pt-0 mt-0"
            active-nav-item-class="font-weight-bolder text-primary active"
          >
            <b-tab>
              <template #title>
                Read ({{ readCount }})
                <div class="line"></div>
              </template>

              <message-log-recipients-list
                :items="readRecipients"
                :loading="loading"
                empty-text="No tradies have read this notification yet."
              />
            </b-tab>

            <b-tab>
              <template #title>
                Not Read ({{ unreadCount }})
                <div class="line"></div>
              </template>

              <message-log-recipients-list
                :items="unreadRecipients"
                :loading="loading"
                empty-text="All tradies have read this notification."
              />
            </b-tab>
          </b-tabs>
        </b-card>

        <b-button
          variant="outline-primary"
          class="message-log-mobile-back-btn d-none mt-3"
          @click="goBackToRequests"
        >
          Back to Requests
        </b-button>
      </div>

      <div class="mt-4" v-if="attachmentUrls.length">
        <b-card>
          <h5 class="font-weight-bold mb-3">Attachments</h5>
          <div class="attachments-section">
            <div class="attachments-list">
              <div
                v-for="(item, i) in attachmentUrls"
                :key="i"
                class="attachment-item"
              >
                <!-- Primary action: view/open attachment -->
                <a
                  :href="item.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="attachment-link"
                  :title="item.name"
                  data-test="attachment-link"
                  @click.prevent="previewIndex = i"
                >
                  <b-icon
                    :icon="attachmentIcon(item.name)"
                    class="attachment-icon"
                  />
                  <span class="attachment-name">{{ item.name }}</span>
                </a>

                <!-- Secondary action: download attachment to device -->
                <a
                  href="#"
                  class="attachment-download-btn"
                  :title="'Download ' + item.name"
                  @click.prevent="downloadAttachment(item, i)"
                >
                  <b-icon icon="download" class="attachment-open-icon" />
                </a>
              </div>
            </div>
          </div>
        </b-card>
      </div>

      <FilePreviewModal
        v-if="previewItem"
        :visible="!!previewItem"
        :url="previewItem.url"
        :name="previewItem.name"
        custom-download
        @download="downloadAttachment(previewItem, previewIndex)"
        @close="previewIndex = null"
      />
    </b-container>
  </transition>
</template>

<script>
import Api from '@/api'
import dayjs from 'dayjs'
import { mapActions, mapGetters, mapState } from 'vuex'
import MessageLogRecipientsList from '@/components/lists/MessageLogRecipientsList.vue'
import FilePreviewModal from '@/components/modals/FilePreviewModal.vue'

export default {
  name: 'ViewMessageLogPage',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    notification: {
      type: Object,
      default: null,
    },
  },
  components: {
    MessageLogRecipientsList,
    FilePreviewModal,
  },

  // Record the route the user came from so "Back to Requests" returns there
  // — the Message Log tab with its filters, or a specific request detail —
  // instead of always hard-pushing the default requests list. Falls back to
  // that list on a direct link / hard reload.
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from && from.name && from.name !== 'request.view-message-log-page') {
        vm.originRoute = from.fullPath
      }
    })
  },

  beforeRouteUpdate(to, from, next) {
    if (from && from.name && from.name !== 'request.view-message-log-page') {
      this.originRoute = from.fullPath
    }
    next()
  },

  data() {
    return {
      // Index into attachmentUrls of the attachment being previewed, or null.
      previewIndex: null,
      tabIndex: 0,
      loading: false,
      error: null,
      log: null,
      // Set by the route guards above; null on a direct link / hard reload.
      originRoute: null,
    }
  },

  watch: {
    '$route.params.id': {
      immediate: true,
      async handler() {
        if (!this.canViewMessageLog) {
          this.redirectIfCannotViewMessageLog()
          return
        }
        await this.fetchLog()
      },
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    ...mapGetters('auth', ['hasPermission']),

    canViewMessageLog() {
      return (
        this.user &&
        this.user.user_type === 'business_admin' &&
        this.hasPermission('requests.message_log.view')
      )
    },

    items() {
      return [
        { text: 'Requests', to: '/requests' },
        { text: 'Message Log Insight' },
      ]
    },

    payload() {
      if (!this.log || !this.log.data) return {}
      if (this.log.data.data && this.log.data.data.data)
        return this.log.data.data.data
      if (this.log.data.data) return this.log.data.data
      return this.log.data
    },

    titleText() {
      return (
        this.payload.title ||
        this.log.title ||
        this.payload.message ||
        this.log.message ||
        '-'
      )
    },

    messageText() {
      return (
        this.payload.body ||
        this.payload.message ||
        this.log.body ||
        this.log.message ||
        '-'
      )
    },

    sentAtText() {
      const value =
        this.log.created_at ||
        this.payload.created_at ||
        this.payload.sent_at ||
        null
      return value ? dayjs(value).format('DD MMM YYYY h:mm A') : '-'
    },

    senderText() {
      const sender =
        this.payload.sender || this.log.sender || this.log.actor || {}
      return (
        this.payload.sender_name ||
        this.log.sender_name ||
        this.log.actor_name ||
        sender.full_name ||
        sender.name ||
        sender.email ||
        '-'
      )
    },

    projectText() {
      const project = this.payload.project || this.log.project || {}
      return (
        this.payload.project_name ||
        this.log.project_name ||
        project.name ||
        '-'
      )
    },

    recipientCountText() {
      return (
        this.allRecipients.length ||
        this.payload.recipient_count ||
        this.log.recipient_count ||
        '-'
      )
    },

    allRecipients() {
      if (!this.log) return []
      const recipients = this.payload.recipients || this.log.recipients || []
      return Array.isArray(recipients) ? recipients : []
    },

    readRecipients() {
      return this.allRecipients.filter((r) => this.isRecipientRead(r))
    },

    unreadRecipients() {
      return this.allRecipients.filter((r) => !this.isRecipientRead(r))
    },

    messageHtml() {
      const safe = this.escapeHtml(this.messageText || '')
      return this.linkify(safe).replace(/\n/g, '<br>')
    },
    readCount() {
      return this.readRecipients.length
    },
    unreadCount() {
      return this.unreadRecipients.length
    },

    previewItem() {
      return this.previewIndex === null
        ? null
        : this.attachmentUrls[this.previewIndex] || null
    },

    attachmentUrls() {
      // On this page, we are viewing a mass notification log fetched by id.
      // Attachments are exposed directly on the log resource as `attachment_urls`,
      // and optionally may also be present on the payload.
      const rawFromLog =
        this.log && Array.isArray(this.log.attachment_urls)
          ? this.log.attachment_urls
          : []
      const rawFromPayload =
        this.payload && Array.isArray(this.payload.attachment_urls)
          ? this.payload.attachment_urls
          : []

      const raw = rawFromLog.length ? rawFromLog : rawFromPayload
      if (!Array.isArray(raw) || !raw.length) return []

      return raw
        .map((item) => {
          if (typeof item === 'string') {
            const name = this.getFileNameFromUrl(item)
            return { url: item, name: name || 'View attachment' }
          }

          const rawName =
            item.name || this.getFileNameFromUrl(item.url) || 'View attachment'
          const displayName = rawName.replace(/^[a-zA-Z0-9]{8}_/, '') || rawName

          return {
            url: item.url,
            name: displayName,
          }
        })
        .filter((item) => item.url)
    },
  },

  methods: {
    ...mapActions('notification', {
      getSentMassLogById: 'getSentMassLogById',
    }),

    redirectIfCannotViewMessageLog() {
      if (this.canViewMessageLog) return

      this.$router.replace({ name: 'requests.list-page' })
    },

    goBackToRequests() {
      if (this.originRoute) {
        this.$router.push(this.originRoute).catch(() => {})
      } else {
        this.$router.push({ name: 'requests.list-page' })
      }
    },

    async fetchLog() {
      if (!this.canViewMessageLog) return

      const id = this.$route.params.id
      if (!id) {
        this.error = 'Missing message log id.'
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = await this.getSentMassLogById(id)
        this.log = response && response.data ? response.data : response
      } catch (err) {
        const apiMessage =
          err && err.response && err.response.data && err.response.data.message
        this.error = apiMessage || 'Unable to load message log.'
        this.log = null
      } finally {
        this.loading = false
      }
    },

    isRecipientRead(recipient) {
      return Boolean(
        recipient.read ||
          recipient.read_at ||
          (recipient.pivot &&
            (recipient.pivot.read || recipient.pivot.read_at)) ||
          recipient.status === 'read',
      )
    },

    escapeHtml(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },

    linkify(text) {
      return text.replace(/((https?:\/\/|www\.)[^\s<]+)/gi, (url) => {
        const href = url.startsWith('http') ? url : `https://${url}`
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`
      })
    },

    getFileNameFromUrl(url) {
      if (!url || typeof url !== 'string') return ''
      try {
        const pathname = new URL(url).pathname
        const segment = pathname.split('/').filter(Boolean).pop() || ''
        // Optional: remove leading "randomId_" (e.g. qimW7J6E_) for a cleaner name
        const withoutPrefix = segment.replace(/^[a-zA-Z0-9]{8}_/, '')
        return decodeURIComponent(withoutPrefix || segment)
      } catch (e) {
        return ''
      }
    },
    attachmentIcon(name) {
      if (!name) return 'file-earmark'
      const n = name.toLowerCase()
      if (n.endsWith('.pdf')) return 'file-earmark-pdf'
      if (n.match(/\.(jpe?g|png|gif|webp|svg)$/)) return 'file-earmark-image'
      return 'file-earmark'
    },

    async downloadAttachment(item, index) {
      if (!this.log || !this.log.id) return

      try {
        const url = `${Api.defaults.baseURL}/notifications/sent/${this.log.id}/attachment/${index}`
        const res = await Api.get(url, { responseType: 'blob' })

        const blob = res.data
        const name = item.name || 'attachment'

        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(link.href)
      } catch (e) {
        // Silently fail for now; you can hook into your toast/alert system if desired
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.white-space-pre-line {
  white-space: pre-line;
}

.header-image {
  // top: 88px;
  // height: 280px;
  background: transparent linear-gradient(130deg, #006cff 0%, #00429d 100%) 0%
    0% no-repeat padding-box;
}

.header-text {
  // top: 136px;
  margin-top: 88px;
  font-size: 35px;
}

/deep/ nav.center-screen {
  margin-top: 0;
}

/deep/ .collapsed {
  .when-open {
    display: none;
  }
}

/deep/ .not-collapsed {
  .when-closed {
    display: none;
  }
}

/deep/ .request-tab .nav-tabs {
  display: inline-flex;
  width: auto;
}

/deep/ .request-tab .nav-item {
  flex: 0 0 180px; // same width both tabs, not full card
  max-width: 180px;
  text-align: center;
}

/deep/ .request-tab {
  a {
    padding: 12px 16px 0 !important;
    margin-bottom: 0px !important;

    & > .line {
      border-bottom: 4px solid transparent;
      border-top-left-radius: 25px;
      border-top-right-radius: 25px;
      margin: 12px 0 0;
    }

    &.active > .line {
      border-bottom: 8px solid #007bff !important;
    }
  }
}

/deep/ .request-tab .nav-tabs {
  display: inline-flex;
  width: auto;
}

/deep/ .request-tab .nav-item {
  flex: 0 0 180px;
  max-width: 180px;
  text-align: center;
}

.attachments-section {
  .attachments-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .attachment-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin-bottom: 0.5rem;

    .attachment-link {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      color: #006cff;
      text-decoration: none;
      &:hover {
        color: #0052cc;
      }
    }
    .attachment-download-btn {
      color: #006cff;
      padding: 0.25rem;
      &:hover {
        color: #0052cc;
      }
    }
  }

  .attachment-icon {
    flex-shrink: 0;
    font-size: 1.25rem;
    color: #64748b;
  }

  .attachment-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .attachment-open-icon {
    flex-shrink: 0;
    font-size: 0.875rem;
    opacity: 0.7;
  }
}

// Small-phone-only tightening. Scoped inside this media query so nothing
// changes at 576px and up.
@media (max-width: 575.98px) {
  // "Back to Requests" moves out of the header (hidden below) and
  // reappears under the Read/Not Read tabs, sized to its content instead
  // of stretching edge-to-edge.
  .message-log-mobile-back-btn {
    display: inline-block !important;
    height: 44px !important;
    padding: 4px 16px !important;
  }

  .message-log-header {
    flex-wrap: wrap;
  }

  .message-log-title {
    font-size: 1.3rem;
    flex: 0 0 100%;
    max-width: 100%;
    margin-bottom: 0 !important;
  }

  .message-log-back-btn {
    display: none !important;
  }

  // The message body / meta columns use only `md`, no `cols`/`sm`, so they
  // have no width class at all below 768px. Force them to stack here.
  .message-log-body-col,
  .message-log-meta-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }

  .message-log-meta-col {
    margin-top: 0.5rem;

    /deep/ .list-group-item {
      font-size: 0.85rem;
      padding-top: 0.35rem;
      padding-bottom: 0.35rem;
    }
  }

  .message-log-notification-title {
    font-size: 1.05rem;
  }

  // The "Read"/"Not Read" tabs are fixed at 180px each (360px total),
  // which overflows a 320px screen. Let them size to their content instead.
  /deep/ .request-tab .nav-item {
    flex: 1 1 auto;
    max-width: none;
  }

  /deep/ .request-tab a {
    padding: 10px 8px 0 !important;
    font-size: 0.8rem;
    white-space: nowrap;
  }

  .attachments-section {
    .attachment-item {
      padding: 0.4rem 0.6rem;
    }

    .attachment-icon {
      font-size: 1.05rem;
    }

    .attachment-name {
      font-size: 0.85rem;
    }
  }
}
</style>
